import type { BlogPostPageType } from '@virginexperiencedays/cms'; // @TODO: This should be agnostic (not aware of outer types)
import { cn } from '@virginexperiencedays/tailwind';

import { DisplayHeading, DisplayHeadingProps } from '../../typography/DisplayHeading';
import { BodyText } from '../../typography/BodyText';
import { LinkWrap } from '../../navigation/LinkWrap';
import { Nugget } from '../../navigation/Nugget';
import { Image } from '../../layout/Image';

export type BlogPostCardProps = {
  post: BlogPostPageType;
  href: string;
  withPriority?: boolean;
  superSized?: boolean;
  SEOHeading?: DisplayHeadingProps['as'];
  className?: string;
};

export const BlogPostCard = ({
  post,
  href,
  withPriority,
  superSized,
  SEOHeading,
  className,
}: BlogPostCardProps) => {
  const { title, parent_link, description, date, author, coverimage } = post;
  const normalizedHref = href.startsWith('/') ? href.replace('/', '') : href;

  const formattedDate = new Date(date).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return (
    <div
      className={cn(
        'grid auto-cols-fr grid-rows-[minmax(0,200px)]',
        superSized &&
          'md:grid-cols-[minmax(0,660px)_minmax(0,440px)] md:grid-rows-[minmax(0,400px)]',
        className
      )}
      key={title}
    >
      <LinkWrap
        href={`/experience-blog/${normalizedHref}`}
        prefetch={false}
        dataTestId="blog-post-link"
      >
        <Image
          className="image:object-cover h-full rounded-lg"
          alt={coverimage?.alt ?? title}
          src={coverimage?.Thumbnail?.url}
          fill
          imageObject={coverimage?.Thumbnail}
          // higher than 990px will use 30vw
          sizes="(min-width: 990px) 30vw, 90vw"
          priority={withPriority}
          dataTestId="post-content-image"
        />
      </LinkWrap>
      <div className={cn('items-star mt-4 flex flex-col', superSized && 'md:ml-8 md:mt-0')}>
        {!!parent_link && (
          <Nugget
            className="w-fit rounded px-1 py-0.5 text-xs uppercase leading-none tracking-wider"
            asLink
            href={`/experience-blog/${parent_link?._meta?.uid}`}
            testId="post-content-nugget"
          >
            {parent_link.title}
          </Nugget>
        )}
        <LinkWrap
          className="mt-2 no-underline"
          href={`/experience-blog/${normalizedHref}`}
          prefetch={false}
          dataTestId="blog-post-link"
        >
          <span className="my-1 no-underline">
            <DisplayHeading
              as={SEOHeading}
              className="text-neutral-strong leading-normal"
              size="4"
              data-testid={'post-content-title'}
            >
              {title}
            </DisplayHeading>
          </span>
          <BodyText
            className={cn('text-neutral mt-1 line-clamp-3', superSized && 'md:line-clamp-[7]')}
            data-testid="post-content-description"
          >
            {/* @TODO: comeback to this, styled components was casting this component to any therefore these errors were silent*/}
            {(description as any) ||
              'Read our curated guide of the best things to do and see, from iconic landmarks to hidden gems and experiences.'}
          </BodyText>
        </LinkWrap>
        <div className="mt-4 flex">
          {author?._meta?.uid && (
            <LinkWrap className="no-underline" href={`/experience-blog/${author?._meta?.uid}`}>
              <BodyText
                className="text-neutral-faded hover:text-link mr-2 text-xs leading-none"
                size="small"
                data-testid="post-content-author"
              >
                {author?.name}
              </BodyText>
            </LinkWrap>
          )}
          <BodyText
            className="text-neutral-faded text-xs leading-none"
            size="small"
            data-testid="post-date"
          >
            {formattedDate || '9 Sept 2021'}
          </BodyText>
        </div>
      </div>
    </div>
  );
};
